// src/pages/admin/SuperAdminUserManagement.js
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { userService, companyService } from '../../services/apiServices';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StatusBadge from '../../components/admin/StatusBadge';
import RoleBadge from '../../components/admin/RoleBadge';
import SearchFilterBar from '../../components/admin/SearchFilterBar';
import DataTable from '../../components/admin/DataTable';
import ConfirmationDialog from '../../components/admin/ConfirmationDialog';
import Pagination from '../../components/admin/Pagination';
import { showSuccessToast, showErrorToast, showLoadingToast, updateToast, showWarningToast } from '../../utils/toastUtils';
import UserLimitRequestsTable from '../../components/admin/UserLimitRequestsTable';
import PasswordRequirements from '../../components/common/PasswordRequirements';
import { validatePassword } from '../../utils/passwordValidator';

const formVariants = {
  hidden: { opacity: 0, y: -30, scale: 0.97 },
  visible: {
    opacity: 1, y: 0, scale: 1,
    transition: { type: 'spring', stiffness: 300, damping: 28 }
  },
  exit: {
    opacity: 0, y: -20, scale: 0.98,
    transition: { duration: 0.15, ease: 'easeIn' }
  }
};

const sectionVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.07, duration: 0.25, ease: 'easeOut' }
  })
};

const SuperAdminUserManagement = () => {
  const { user: currentUser } = useAuth();
  const formRef = useRef(null);

  // State for users
  const [users, setUsers] = useState([]);

  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Form data state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'simpleuser',
    status: 'active',
    companyId: null,
    mfaRequired: false
  });

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Editing state
  const [isEditing, setIsEditing] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Scroll to form when it opens
  useEffect(() => {
    if (showForm && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showForm]);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  // Confirmation dialog state
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Limits modal state
  const [showLimitsModal, setShowLimitsModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [limitsFormData, setLimitsFormData] = useState({
    maxSimpleUsers: ''
  });

  // Companies for dropdown
  const [companies, setCompanies] = useState([]);

  // User limit requests
  const [userLimitRequests, setUserLimitRequests] = useState([]);
  const [showRequestsSection, setShowRequestsSection] = useState(false);

  // Fetch users and companies on component mount or when filters change
  useEffect(() => {
    fetchUsers();
  }, [pagination.page, pagination.limit, searchTerm, roleFilter, statusFilter]);

  useEffect(() => {
    fetchCompanies();
    fetchUserLimitRequests();
  }, []);

  // Function to fetch companies
  const fetchCompanies = async () => {
    try {
      const response = await companyService.getCompanies({ limit: 1000 });
      if (response.success) {
        setCompanies(response.data || []);
      } else {
        // Fallback: get companies through admin users
        const usersResponse = await userService.getUsers({ role: 'admin' });
        if (usersResponse.success) {
          const uniqueCompanies = [];
          const seenCompanies = new Set();

          usersResponse.data.forEach(user => {
            if (user.companyId && user.companyName && !seenCompanies.has(user.companyId)) {
              uniqueCompanies.push({
                _id: user.companyId,
                name: user.companyName
              });
              seenCompanies.add(user.companyId);
            }
          });

          setCompanies(uniqueCompanies);
        }
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    }
  };

  // Function to fetch user limit requests
  const fetchUserLimitRequests = async () => {
    try {
      // TODO: Replace with actual API call to get user limit requests
      // const response = await subscriptionRequestService.getAllRequests({ 
      //   requestType: 'user_limit_increase' 
      // });
      // setUserLimitRequests(response.data || []);

      // For now, no mock data - only show real requests
      setUserLimitRequests([]);
    } catch (error) {
      console.error('Error fetching user limit requests:', error);
      setUserLimitRequests([]);
    }
  };

  // Function to fetch users from API
  const fetchUsers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Prepare filters for API - only include defined values
      const filters = {
        page: pagination.page,
        limit: pagination.limit
      };

      if (searchTerm) filters.search = searchTerm;
      if (roleFilter !== 'all') filters.role = roleFilter;
      if (statusFilter !== 'all') filters.status = statusFilter;

      const response = await userService.getUsers(filters);

      if (response.success) {
        setUsers(response.data || []);
        // Update pagination from response
        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            ...response.pagination
          }));
        } else {
          // Fallback
          setPagination(prev => ({
            ...prev,
            total: response.data?.length || 0,
            pages: Math.ceil((response.data?.length || 0) / prev.limit)
          }));
        }
      } else {
        throw new Error(response.message || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Erreur lors du chargement des utilisateurs. Veuillez réessayer plus tard.');
      showErrorToast('Erreur lors du chargement des utilisateurs');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filter and sort users
  // Note: Filtering is now largely handled on the server side
  const filteredUsers = [...users]
    .sort((a, b) => {
      if (!sortConfig.key) return 0;

      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle date sorting
      if (sortConfig.key === 'lastLogin' || sortConfig.key === 'createdAt') {
        if (!aValue && !bValue) return 0;
        if (!aValue) return sortConfig.direction === 'asc' ? 1 : -1;
        if (!bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Handle numeric sorting
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle string sorting
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      return 0;
    });

  // Calculate metrics
  const totalUsersMetric = pagination.total || users.length;
  const activeUsers = users.filter(user => user.status === 'active').length; // Local page view only, but dashboard is better
  const superAdmins = users.filter(user => user.role === 'superadmin').length;
  const admins = users.filter(user => user.role === 'admin').length;
  const simpleUsers = users.filter(user => user.role === 'simpleuser').length;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Convert empty string to null for companyId to properly handle "no company" selection
    const processedValue = name === 'companyId' && value === '' ? null : (type === 'checkbox' ? checked : value);

    setFormData({
      ...formData,
      [name]: processedValue
    });
  };

  const handleLimitsInputChange = (e) => {
    const { name, value } = e.target;
    setLimitsFormData({
      ...limitsFormData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      showWarningToast('Le nom est requis');
      return;
    }
    if (!formData.email.trim()) {
      showWarningToast('L\'email est requis');
      return;
    }
    if (!isEditing && !formData.password.trim()) {
      showWarningToast('Le mot de passe est requis');
      return;
    }
    if ((!isEditing || formData.password.trim()) && !validatePassword(formData.password).isValid) {
      showWarningToast('Le mot de passe doit comporter au moins 8 caractères, une majuscule, une minuscule, un chiffre et un symbole');
      return;
    }

    const action = isEditing ? 'Mise à jour' : 'Création';
    const loadingToastId = showLoadingToast(`${action} de l'utilisateur en cours...`);

    setIsSubmitting(true);
    setError(null);

    try {
      const userData = { ...formData };

      if (isEditing) {
        // For updates, handle password separately if provided
        const hasPasswordUpdate = userData.password && userData.password.trim();

        // Remove password from main user data - it should be updated separately
        delete userData.password;

        // Only send fields that have actually changed to avoid unintended updates
        const currentUser = users.find(u => u._id === currentUserId);
        const changedFields = {};

        // Only include fields that are different from current values
        if (userData.name !== currentUser.name) changedFields.name = userData.name;
        if (userData.email !== currentUser.email) changedFields.email = userData.email;
        if (userData.role !== currentUser.role) changedFields.role = userData.role;
        if (userData.status !== currentUser.status) changedFields.status = userData.status;

        // Only include companyId if it's actually being changed
        // Handle both null and string comparisons properly
        const currentCompanyId = currentUser.companyId || null;
        const newCompanyId = userData.companyId || null;

        if (currentCompanyId !== newCompanyId) {
          changedFields.companyId = newCompanyId;
        }

        // Debug logging
        console.log('User update debug:', {
          currentUser: {
            id: currentUser._id,
            name: currentUser.name,
            email: currentUser.email,
            role: currentUser.role,
            status: currentUser.status,
            companyId: currentUser.companyId,
            companyName: currentUser.companyName
          },
          formData: userData,
          changedFields,
          hasPasswordUpdate
        });

        // Update user basic information first (only if there are changes)
        let response = { success: true, data: currentUser };
        if (Object.keys(changedFields).length > 0) {
          console.log('Sending update request with fields:', changedFields);
          response = await userService.updateUser(currentUserId, changedFields);
        } else {
          console.log('No field changes detected, skipping user update');
        }

        if (response.success) {
          // If password was provided, update it separately
          if (hasPasswordUpdate) {
            try {
              await userService.updatePassword(currentUserId, {
                newPassword: formData.password
              });
            } catch (passwordError) {
              console.error('Password update error:', passwordError);
              // User data was updated successfully, but password failed
              updateToast(loadingToastId, 'Utilisateur mis à jour, mais erreur lors du changement de mot de passe', 'warning');
              setUsers(
                users.map((user) =>
                  user._id === currentUserId
                    ? { ...user, ...response.data }
                    : user
                )
              );
              resetForm();
              return;
            }
          }

          setUsers(
            users.map((user) =>
              user._id === currentUserId
                ? { ...user, ...response.data }
                : user
            )
          );

          const successMessage = hasPasswordUpdate
            ? 'Utilisateur et mot de passe mis à jour avec succès'
            : 'Utilisateur mis à jour avec succès';
          updateToast(loadingToastId, successMessage, 'success');

          // Verify company association was preserved/updated correctly
          const updatedUserData = response.data;
          if (changedFields.companyId !== undefined) {
            if (changedFields.companyId && !updatedUserData.companyId) {
              console.warn('⚠️  Company assignment may have failed');
            } else if (!changedFields.companyId && updatedUserData.companyId) {
              console.warn('⚠️  Company removal may have failed');
            }
          }
          resetForm();
        } else {
          throw new Error(response.message || 'Failed to update user');
        }
      } else {
        // Create new user
        const response = await userService.createUser(userData);

        if (response.success) {
          setUsers([...users, response.data]);
          updateToast(loadingToastId, 'Utilisateur créé avec succès', 'success');
          resetForm();
        } else {
          throw new Error(response.message || 'Failed to create user');
        }
      }
    } catch (error) {
      console.error(isEditing ? 'Update user error:' : 'Create user error:', error);
      const errorMessage = `Erreur lors de ${isEditing ? 'la mise à jour' : 'la création'} de l'utilisateur. ${error.message || 'Veuillez réessayer.'}`;
      setError(errorMessage);
      updateToast(loadingToastId, errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (user) => {
    if (!user || typeof user !== 'object') {
      console.error('Invalid user object:', user);
      return;
    }

    setFormData({
      name: user.name || '',
      email: user.email || '',
      password: '',
      role: user.role || 'simpleuser',
      status: user.status || 'active',
      companyId: user.companyId || null,
      mfaRequired: user.mfa?.requiredByAdmin || false
    });

    setCurrentUserId(user._id);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleStatusToggle = async (user) => {
    const action = user.status === 'active' ? 'désactiver' : 'activer';
    const loadingToastId = showLoadingToast(`${action.charAt(0).toUpperCase() + action.slice(1)} l'utilisateur...`);

    setIsSubmitting(true);

    try {
      const updatedStatus = user.status === 'active' ? 'inactive' : 'active';

      const response = await userService.updateUser(user._id, { status: updatedStatus });

      if (response.success) {
        setUsers(
          users.map((u) =>
            u._id === user._id
              ? { ...u, status: updatedStatus }
              : u
          )
        );
        updateToast(loadingToastId, `Utilisateur ${updatedStatus === 'active' ? 'activé' : 'désactivé'} avec succès`, 'success');
      } else {
        throw new Error(response.message || 'Failed to update user status');
      }
    } catch (error) {
      console.error('Update user status error:', error);
      const errorMessage = 'Erreur lors de la mise à jour du statut. Veuillez réessayer.';
      setError(errorMessage);
      updateToast(loadingToastId, errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowConfirmation(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    setIsSubmitting(true);
    setShowConfirmation(false);

    try {
      const response = await userService.deleteUser(userToDelete._id);

      if (response.success) {
        setUsers(users.filter(u => u._id !== userToDelete._id));
        setUserToDelete(null);
        showSuccessToast('Utilisateur supprimé avec succès');
      } else {
        throw new Error(response.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Delete user error:', error);
      const errorMessage = 'Erreur lors de la suppression de l\'utilisateur. Veuillez réessayer.';
      setError(errorMessage);
      showErrorToast(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetLimits = (admin) => {
    setSelectedAdmin(admin);
    setLimitsFormData({
      maxSimpleUsers: admin.userCreationLimits?.maxSimpleUsers?.toString() || ''
    });
    setShowLimitsModal(true);
  };

  const handleLimitsSubmit = async (e) => {
    e.preventDefault();

    if (!selectedAdmin) return;

    const loadingToastId = showLoadingToast('Mise à jour des limites en cours...');
    setIsSubmitting(true);

    try {
      const maxSimpleUsers = limitsFormData.maxSimpleUsers === '' ? null : parseInt(limitsFormData.maxSimpleUsers);

      const response = await userService.updateUserCreationLimits(selectedAdmin._id, { maxSimpleUsers });

      if (response.success) {
        // Update the user in the local state
        setUsers(users.map(user =>
          user._id === selectedAdmin._id
            ? { ...user, userCreationLimits: response.data.userCreationLimits }
            : user
        ));

        updateToast(loadingToastId, 'Limites mises à jour avec succès', 'success');
        setShowLimitsModal(false);
        setSelectedAdmin(null);
      } else {
        throw new Error(response.message || 'Failed to update limits');
      }
    } catch (error) {
      console.error('Update limits error:', error);
      const errorMessage = 'Erreur lors de la mise à jour des limites. Veuillez réessayer.';
      updateToast(loadingToastId, errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'simpleuser',
      status: 'active',
      companyId: null,
      mfaRequired: false
    });
    setIsEditing(false);
    setCurrentUserId(null);
    setShowForm(false);
    setError(null);
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    switch (key) {
      case 'role':
        setRoleFilter(value);
        break;
      case 'status':
        setStatusFilter(value);
        break;
      default:
        break;
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
    setStatusFilter('all');
  };

  // Table columns definition
  const columns = [
    {
      key: 'name',
      label: 'Nom',
      sortable: true,
      render: (value) => <span className="font-medium text-gray-900 text-sm">{value || '-'}</span>
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      render: (value) => <span className="text-gray-500 text-sm">{value || '-'}</span>
    },
    {
      key: 'role',
      label: 'Rôle',
      sortable: true,
      render: (value) => <RoleBadge role={value} size="sm" />
    },
    {
      key: 'companyName',
      label: 'Entreprise',
      sortable: true,
      render: (value) => <span className="text-gray-500 text-sm">{value || 'Aucune'}</span>
    },
    {
      key: 'status',
      label: 'Statut',
      sortable: true,
      render: (value) => <StatusBadge status={value === 'active' ? 'Actif' : 'Inactif'} type={value === 'active' ? 'success' : 'danger'} size="sm" />
    },
    {
      key: 'userCreationLimits',
      label: 'Limites Utilisateurs',
      sortable: false,
      render: (value, user) => {
        if (user.role !== 'admin') return <span className="text-gray-400 text-sm">-</span>;

        const limits = value || {};
        const max = limits.maxSimpleUsers;
        const current = limits.currentSimpleUsers || 0;

        return (
          <div className="text-sm">
            <span className="text-gray-600">
              {current}/{max === null ? '∞' : max}
            </span>
          </div>
        );
      }
    },
    {
      key: 'cguAccepted',
      label: 'CGU',
      sortable: true,
      render: (value) => (
        <div className="flex items-center justify-center text-sm font-bold" title={value ? 'Conditions acceptées' : 'Conditions non acceptées'}>
          {value ? (
            <span className="text-green-700 bg-green-100 border border-green-200 px-2 py-1 rounded-md flex items-center shadow-sm">
              <span className="mr-1">✅</span> Oui
            </span>
          ) : (
            <span className="text-gray-600 bg-gray-100 border border-gray-200 px-2 py-1 rounded-md flex items-center shadow-sm">
              <span className="mr-1">❌</span> Non
            </span>
          )}
        </div>
      )
    },
    {
      key: 'lastLogin',
      label: 'Dernière connexion',
      sortable: true,
      render: (value) => <span className="text-gray-500 text-sm">{value ? new Date(value).toLocaleString() : 'Jamais'}</span>
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (_, user) => (
        <div className="flex flex-wrap gap-1">
          <motion.button
            onClick={() => handleEdit(user)}
            className="text-xs text-indigo-600 hover:text-white hover:bg-indigo-600 font-medium px-2.5 py-1.5 rounded transition-all duration-150 flex items-center gap-1"
            disabled={isSubmitting}
            whileTap={{ scale: 0.95 }}
          >
            <i className="fas fa-edit text-xs"></i>
            <span>Modifier</span>
          </motion.button>

          {user.role === 'admin' && (
            <button
              onClick={() => handleSetLimits(user)}
              className="text-xs text-purple-600 hover:text-white hover:bg-purple-600 font-medium px-2.5 py-1.5 rounded transition-all duration-150 flex items-center gap-1"
              disabled={isSubmitting}
            >
              <i className="fas fa-cog text-xs"></i>
              <span>Limites</span>
            </button>
          )}

          <button
            onClick={() => handleStatusToggle(user)}
            className={`text-xs font-medium px-2.5 py-1.5 rounded transition-all duration-150 flex items-center gap-1 ${user.status === 'active'
              ? 'text-red-600 hover:text-white hover:bg-red-600'
              : 'text-green-600 hover:text-white hover:bg-green-600'
              }`}
            disabled={isSubmitting}
          >
            <i className={`fas text-xs ${user.status === 'active' ? 'fa-ban' : 'fa-check'}`}></i>
            <span>{user.status === 'active' ? 'Désactiver' : 'Activer'}</span>
          </button>

          <button
            onClick={() => handleDeleteClick(user)}
            className="text-xs text-red-600 hover:text-white hover:bg-red-600 font-medium px-2.5 py-1.5 rounded transition-all duration-150 flex items-center gap-1"
            disabled={isSubmitting}
          >
            <i className="fas fa-trash text-xs"></i>
            <span>Supprimer</span>
          </button>
        </div>
      )
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-indigo-500 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-sm text-gray-500">Chargement des utilisateurs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Gestion des Utilisateurs
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Administration complète des utilisateurs du système</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowRequestsSection(!showRequestsSection)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${showRequestsSection
              ? 'bg-orange-500 text-white hover:bg-orange-600'
              : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
              } focus:outline-none focus:ring-2 focus:ring-orange-300`}
            disabled={isSubmitting}
          >
            <i className={`fas ${showRequestsSection ? 'fa-eye-slash' : 'fa-bell'} text-xs`}></i>
            <span>{showRequestsSection ? 'Masquer' : `Demandes (${userLimitRequests.filter(r => r.status === 'pending').length})`}</span>
          </button>
          <button
            onClick={() => setShowForm(!showForm)}
            className={`px-3 py-2 rounded-lg text-sm font-medium text-white transition-colors flex items-center gap-1.5 ${showForm
              ? 'bg-red-500 hover:bg-red-600'
              : 'bg-indigo-600 hover:bg-indigo-700'
              } focus:outline-none focus:ring-2 focus:ring-indigo-300`}
            disabled={isSubmitting}
          >
            <i className={`fas ${showForm ? 'fa-times' : 'fa-user-plus'} text-xs`}></i>
            <span>{showForm ? 'Annuler' : 'Nouvel Utilisateur'}</span>
          </button>
        </div>
      </div>

      {/* Metrics Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total</p>
          <p className="text-2xl font-bold text-gray-900 mt-0.5">{totalUsersMetric}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Actifs</p>
          <p className="text-2xl font-bold text-green-600 mt-0.5">{activeUsers}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">SuperAdmins</p>
          <p className="text-2xl font-bold text-red-600 mt-0.5">{superAdmins}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Admins</p>
          <p className="text-2xl font-bold text-purple-600 mt-0.5">{admins}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Simples</p>
          <p className="text-2xl font-bold text-yellow-600 mt-0.5">{simpleUsers}</p>
        </div>
      </div>

      {/* User Limit Requests Section */}
      {showRequestsSection && (
        <div className="mb-6">
          <UserLimitRequestsTable
            requests={userLimitRequests.filter(r => r.requestType === 'user_limit_increase')}
            onRequestUpdate={() => {
              fetchUserLimitRequests();
              fetchUsers(); // Refresh users to get updated limits
            }}
          />
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-3">
          <i className="fas fa-exclamation-circle text-red-500"></i>
          <span className="text-sm flex-1">{error}</span>
          <button
            onClick={fetchUsers}
            className="text-sm bg-red-600 text-white px-3 py-1.5 rounded hover:bg-red-700 transition-colors whitespace-nowrap"
          >
            <i className="fas fa-redo mr-1.5"></i>
            Réessayer
          </button>
        </div>
      )}

      {/* User Form with Animations */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            key="user-form"
            ref={formRef}
            variants={formVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6 relative"
          >
            {isSubmitting && (
              <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-indigo-500 border-t-transparent"></div>
                  <p className="text-sm text-gray-600">Traitement en cours...</p>
                </div>
              </div>
            )}

            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">
                <i className={`fas ${isEditing ? 'fa-edit' : 'fa-user-plus'} text-indigo-600 mr-2`}></i>
                {isEditing ? 'Modifier l\'utilisateur' : 'Créer un nouvel utilisateur'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {/* Personal Information */}
              <motion.div custom={0} variants={sectionVariants} initial="hidden" animate="visible" className="border border-gray-200 rounded-lg bg-gray-50/50">
                <div className="px-4 py-2.5 border-b border-gray-200">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Informations personnelles</h3>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Nom complet *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      required
                      disabled={isSubmitting}
                      placeholder="Entrez le nom complet"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      required
                      disabled={isSubmitting}
                      placeholder="utilisateur@exemple.com"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Security & Access */}
              <motion.div custom={1} variants={sectionVariants} initial="hidden" animate="visible" className="border border-gray-200 rounded-lg bg-gray-50/50">
                <div className="px-4 py-2.5 border-b border-gray-200">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Sécurité et accès</h3>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {isEditing ? 'Nouveau mot de passe (optionnel)' : 'Mot de passe *'}
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      required={!isEditing}
                      disabled={isSubmitting}
                      placeholder={isEditing ? "Laisser vide pour ne pas changer" : "Mot de passe sécurisé"}
                    />
                    {(!isEditing || formData.password) && (
                      <PasswordRequirements password={formData.password} />
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Rôle</label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white"
                      disabled={isSubmitting}
                    >
                      <option value="superadmin">Super Administrateur</option>
                      <option value="admin">Administrateur</option>
                      <option value="simpleuser">Utilisateur Simple</option>
                    </select>
                  </div>
                </div>
              </motion.div>

              {/* Status & Company */}
              <motion.div custom={2} variants={sectionVariants} initial="hidden" animate="visible" className="border border-gray-200 rounded-lg bg-gray-50/50">
                <div className="px-4 py-2.5 border-b border-gray-200">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Statut et entreprise</h3>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Statut</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white"
                      disabled={isSubmitting}
                    >
                      <option value="active">Actif</option>
                      <option value="inactive">Inactif</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Entreprise</label>
                    <select
                      name="companyId"
                      value={formData.companyId || ''}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white"
                      disabled={isSubmitting}
                    >
                      <option value="">Aucune entreprise</option>
                      {companies.map((company) => (
                        <option key={company._id} value={company._id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </motion.div>

              {/* MFA obligatoire */}
              <motion.div custom={3} variants={sectionVariants} initial="hidden" animate="visible" className="border border-gray-200 rounded-lg bg-gray-50/50">
                <div className="px-4 py-2.5 border-b border-gray-200">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Sécurité</h3>
                </div>
                <div className="p-4">
                  <div className="flex items-end">
                    <label className="flex items-center gap-3 cursor-pointer py-2.5 px-4 rounded-lg border border-gray-200 hover:border-indigo-300 transition-all duration-200 w-full bg-white/50 hover:bg-white">
                      <input
                        type="checkbox"
                        name="mfaRequired"
                        checked={formData.mfaRequired}
                        onChange={handleInputChange}
                        className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <div>
                        <span className="text-sm font-semibold text-gray-700">MFA obligatoire</span>
                        <p className="text-xs text-gray-400 mt-0.5">Configurer à la prochaine connexion</p>
                      </div>
                    </label>
                  </div>
                </div>
              </motion.div>

              {/* Action Buttons */}
              <motion.div custom={3} variants={sectionVariants} initial="hidden" animate="visible" className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                  disabled={isSubmitting}
                >
                  <i className="fas fa-times mr-1.5"></i>
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Traitement...
                    </>
                  ) : (
                    <>
                      <i className={`fas ${isEditing ? 'fa-save' : 'fa-user-plus'} text-xs`}></i>
                      {isEditing ? 'Mettre à jour' : 'Créer l\'utilisateur'}
                    </>
                  )}
                </button>
              </motion.div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search and filters */}
      <SearchFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filters={[
          {
            key: 'role',
            label: 'Filtrer par rôle',
            type: 'select',
            value: roleFilter,
            options: [
              { value: 'all', label: 'Tous les rôles' },
              { value: 'superadmin', label: 'Super Administrateur' },
              { value: 'admin', label: 'Administrateur' },
              { value: 'simpleuser', label: 'Utilisateur Simple' }
            ]
          },
          {
            key: 'status',
            label: 'Filtrer par statut',
            type: 'select',
            value: statusFilter,
            options: [
              { value: 'all', label: 'Tous les statuts' },
              { value: 'active', label: 'Actif' },
              { value: 'inactive', label: 'Inactif' }
            ]
          }
        ]}
        onFilterChange={handleFilterChange}
        onResetFilters={resetFilters}
        placeholder="Rechercher par nom ou email..."
      />

      {/* Data table */}
      <DataTable
        data={filteredUsers}
        columns={columns}
        isLoading={isLoading}
        onSort={handleSort}
        sortConfig={sortConfig}
        emptyMessage="Aucun utilisateur trouvé."
        searchQuery={searchTerm}
        filters={{ role: roleFilter, status: statusFilter }}
      />

      {/* Pagination */}
      <Pagination
        currentPage={pagination.page}
        totalPages={pagination.pages}
        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
      />

      {/* Results summary */}
      {filteredUsers.length > 0 && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 rounded-b-lg text-sm text-gray-600">
          Affichage de {filteredUsers.length} utilisateur(s) sur {pagination.total} au total
        </div>
      )}

      {/* Limits Modal */}
      {showLimitsModal && selectedAdmin && (
        <div className="fixed inset-0 bg-black/50 overflow-y-auto h-full w-full z-50 backdrop-blur-sm">
          <div className="relative top-20 mx-auto border-0 w-96 shadow-xl rounded-lg bg-white">
            {isSubmitting && (
              <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-indigo-500 border-t-transparent"></div>
                  <p className="text-sm text-gray-600">Mise à jour...</p>
                </div>
              </div>
            )}

            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900">
                <i className="fas fa-cog text-purple-600 mr-2"></i>
                Limites pour {selectedAdmin.name}
              </h3>
            </div>

            <form onSubmit={handleLimitsSubmit} className="p-5 space-y-4">
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Nombre maximum d'utilisateurs simples
                </label>
                <input
                  type="number"
                  name="maxSimpleUsers"
                  value={limitsFormData.maxSimpleUsers}
                  onChange={handleLimitsInputChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                  placeholder="Laisser vide pour illimité"
                  min="0"
                  disabled={isSubmitting}
                />
                <div className="mt-3 p-3 bg-white rounded-lg border border-purple-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Utilisateurs actuels:</span>
                    <span className="font-semibold text-purple-600">
                      {selectedAdmin.userCreationLimits?.currentSimpleUsers || 0}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-sm text-gray-600">Limite actuelle:</span>
                    <span className="font-semibold text-gray-800">
                      {selectedAdmin.userCreationLimits?.maxSimpleUsers || '∞'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowLimitsModal(false);
                    setSelectedAdmin(null);
                  }}
                  className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                  disabled={isSubmitting}
                >
                  <i className="fas fa-times mr-1.5"></i>
                  <span>Annuler</span>
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Enregistrement...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      <span>Enregistrer</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmation}
        onClose={() => {
          setShowConfirmation(false);
          setUserToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Supprimer l'utilisateur"
        message={`Êtes-vous sûr de vouloir supprimer l'utilisateur "${userToDelete?.name}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        confirmButtonClass="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
        cancelButtonClass="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
      />
    </div>
  );
};

export default SuperAdminUserManagement;